import { DRAG_HANDLE_SELECTOR } from '@ardoq/button';
import {
  DragAndDropDragState,
  DragAndDropOrientation,
  DropIndicatorTarget,
  OverlayIndicatorOrientation,
  Position,
  ReorderingDragState,
} from './types';

export const hasOverlayIndicator = (
  orientation: DragAndDropOrientation
): orientation is OverlayIndicatorOrientation =>
  orientation === 'grid' || orientation === 'wrap';

export const arrayMove = <T>(
  array: T[],
  fromIndex: number,
  toIndex: number
): T[] => {
  const arrayCopy = [...array];
  arrayCopy.splice(toIndex, 0, arrayCopy.splice(fromIndex, 1)[0]);
  return arrayCopy;
};

/**
 * Whether a drag originated from a drag handle. `closest` is used so a drag
 * started on something nested inside the handle — the icon, say — still counts.
 */
export const isTargetDragHandleButton = (target: EventTarget) =>
  target instanceof Element && target.closest(DRAG_HANDLE_SELECTOR) !== null;

type RectOrigin = Pick<DOMRect, 'left' | 'top'>;

type DropTargetRect = RectOrigin & Pick<DOMRect, 'width' | 'height'>;

export type DragImageOffset = { offsetX: number; offsetY: number };

export const getDragImageOffset = (
  rect: RectOrigin,
  clientX: number,
  clientY: number
): DragImageOffset => ({
  offsetX: clientX - rect.left,
  offsetY: clientY - rect.top,
});

export const getDropPosition = (
  rect: DropTargetRect,
  clientX: number,
  clientY: number,
  orientation: DragAndDropOrientation
): Position => {
  if (orientation === 'vertical') {
    const middle = rect.top + rect.height / 2;
    return middle > clientY ? 'before' : 'after';
  }
  const middle = rect.left + rect.width / 2;
  return middle > clientX ? 'before' : 'after';
};

export const toDropIndicatorTarget = (
  currentOrder: string[],
  { draggedOverCardId, position }: DragAndDropDragState
): DropIndicatorTarget | null => {
  if (!draggedOverCardId || !position) return null;
  if (position === 'before') return { cardId: draggedOverCardId, position };

  const draggedOverIndex = currentOrder.indexOf(draggedOverCardId);
  const followingCardId =
    draggedOverIndex === -1 ? undefined : currentOrder[draggedOverIndex + 1];
  if (!followingCardId) return { cardId: draggedOverCardId, position: 'after' };
  return { cardId: followingCardId, position: 'before' };
};

export const isReorderingDrop = (
  currentOrder: string[],
  dragState: DragAndDropDragState
): dragState is ReorderingDragState => {
  const { draggedCardId, draggedOverCardId, position } = dragState;
  if (!draggedCardId || !draggedOverCardId || !position) return false;
  if (draggedCardId === draggedOverCardId) return false;
  const draggedIndex = currentOrder.indexOf(draggedCardId);
  const overIndex = currentOrder.indexOf(draggedOverCardId);
  if (draggedIndex === -1 || overIndex === -1) return false;
  return !(
    (position === 'after' && overIndex === draggedIndex - 1) ||
    (position === 'before' && overIndex === draggedIndex + 1)
  );
};
